/**
 * 错误日志工具类
 * 提供更丰富的错误信息和上下文收集功能
 * 集成 Sentry 异常上报功能
 *
 * prompt
 - 你是 Web 前端稳定性专家，我希望捕获异常发生时很多的堆栈信息或者上下文信息，有没有更好的方式替换 console.error
 - @errorLogger.ts 如何结合使用 Sentry captureException 的方式上报异常
 */
import { isProd } from '@umu/ukit-lang';
import { getNetworkStatus, isSlowNetwork, INetworkInfo } from './networkInfo';

let isPageUnloading = false;
// 监听页面卸载，标记状态（用于判断请求中断是否由页面卸载导致）
if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => {
        isPageUnloading = true;
    });
}

const getCurrentPageStatus = () => {
    return {
        isPageUnloading,
        pageVisibility: document?.visibilityState || (document?.hidden ? 'hidden' : 'visible'),
    };
}

export interface ErrorContext {
    componentName: string;
    props?: Record<string, any>;
    state?: Record<string, any>;
    networkInfo?: {
        status: INetworkInfo;
        isSlow: boolean;
    };
    userAgent?: string;
    timestamp: number;
    url?: string;
    stackTrace?: string;
    additionalInfo?: Record<string, any>;
}

export interface ErrorLogData {
    error: Error | Event | string;
    context: ErrorContext;
    severity: 'error' | 'warn' | 'info';
    category: string;
}

class ErrorLogger {
    private static instance: ErrorLogger;

    private constructor() {
        // 私有构造函数，确保单例模式
    }

    public static getInstance(): ErrorLogger {
        if (!ErrorLogger.instance) {
            ErrorLogger.instance = new ErrorLogger();
        }
        return ErrorLogger.instance;
    }

    /**
     * 收集错误上下文信息
     */
    private collectErrorContext(
        componentName: string = 'unknown',
        props?: Record<string, any>,
        state?: Record<string, any>,
        additionalInfo?: Record<string, any>,
    ): ErrorContext {
        const context: ErrorContext = {
            componentName,
            props: Object.assign(this.sanitizeData(props), getCurrentPageStatus()),
            state: this.sanitizeData(state),
            networkInfo: {
                status: getNetworkStatus?.() || {},
                isSlow: isSlowNetwork?.() || false,
            },
            userAgent: navigator.userAgent,
            timestamp: Date.now(),
            url: window.location.href,
            stackTrace: this.getStackTrace(),
            additionalInfo: this.sanitizeData(additionalInfo),
        };

        return context;
    }

    /**
     * 获取堆栈跟踪信息
     */
    private getStackTrace(): string {
        try {
            const error = new Error();
            return error.stack || '';
        } catch (error) {
            return 'Unable to get stack trace';
        }
    }

    /**
     * 清理敏感数据
     */
    private sanitizeData(data?: Record<string, any>): Record<string, any> {
        if (!data) {
            return {};
        }

        const sanitized: Record<string, any> = {};
        const sensitiveKeys = ['password', 'token', 'secret', 'key', 'auth'];

        for (const [key, value] of Object.entries(data)) {
            if (sensitiveKeys.some((sensitiveKey) => key.toLowerCase().includes(sensitiveKey))) {
                sanitized[key] = '[REDACTED]';
            } else {
                sanitized[key] = value;
            }
        }

        return sanitized;
    }

    /**
     * 记录错误信息
     */
    public logError(
        error: Error | Event | string,
        componentName: string = 'unknown',
        props?: Record<string, any>,
        state?: Record<string, any>,
        additionalInfo?: Record<string, any>,
    ): void {
        // 页面卸载时发生的错误无需上报
        if (isPageUnloading) {
            return;
        }

        try {
            const context = this.collectErrorContext(componentName, props, state, additionalInfo);
            if (isProd) {
                // 上报到 Sentry
                this.reportToSentry(error, context);
            } else {
                this.outputError({
                    error,
                    context,
                    severity: 'error',
                    category: 'component',
                });
            }
        } catch (logError) {
            // 如果日志记录本身出错，使用最基本的console.error
            console.error('ErrorLogger failed:', logError, 'Original error:', error);
        }
    }

    /**
     * 上报错误到 Sentry
     */
    private reportToSentry(error: Error | Event | string, context: ErrorContext): void {
        if (!window.Sentry) {
            console.error('Sentry is not initialized');
            return;
        }
        try {
            // 准备 Sentry 上下文信息
            const sentryContext = {
                mechanism: {
                    type: 'generic',
                    handled: true,
                },
                tags: {
                    component: context.componentName,
                    network_status: context.networkInfo?.status,
                    network_slow: context.networkInfo?.isSlow,
                },
                extra: {
                    props: context.props,
                    state: context.state,
                    additionalInfo: context.additionalInfo,
                    networkInfo: context.networkInfo,
                },
                contexts: {
                    component: {
                        name: context.componentName,
                        props: context.props,
                        state: context.state,
                    },
                    network: {
                        status: context.networkInfo?.status,
                        isSlow: context.networkInfo?.isSlow,
                    },
                },
            };

            // 根据错误类型选择上报方式
            if (error instanceof Error) {
                // 对于 Error 对象，使用 captureException
                Sentry.captureException(error, {
                    ...sentryContext,
                    mechanism: {
                        type: 'generic',
                        handled: true,
                    },
                });
            } else if (error instanceof Event) {
                // 对于 Event 对象，创建自定义错误
                const customError = new Error(`Event Error: ${error.type}`);
                customError.name = 'EventError';
                Sentry.captureException(customError, {
                    ...sentryContext,
                    mechanism: {
                        type: 'generic',
                        handled: true,
                    },
                });
            } else {
                // 对于字符串或其他类型，使用 captureMessage
                window.Sentry?.captureMessage?.(String(error), {
                    level: 'error',
                    ...sentryContext,
                    mechanism: {
                        type: 'generic',
                        handled: true,
                    },
                });
            }
        } catch (sentryError) {
            console.error('Sentry report error:', sentryError);
        }
    }

    /**
     * 输出错误信息
     * 测试环境输出详细错误信息，生产环境不输出
     */
    private outputError(logData: ErrorLogData): void {
        const { error, context } = logData;
        // 构建详细的错误信息
        const errorMessage = this.buildErrorMessage(error);
        console.group('🔍 Detailed Error Context');
        console.error(errorMessage);
        console.log(
            'Component:',
            context.componentName,
            'URL:',
            context.url,
            'Network Info:',
            context.networkInfo,
            'Props:',
            context.props,
            'State:',
            context.state,
            'Stack Trace:',
            context.stackTrace,
        );
        if (context.additionalInfo) {
            console.error('Additional Info:', context.additionalInfo);
        }
        console.groupEnd();
    }

    /**
     * 构建错误消息
     */
    private buildErrorMessage(error: Error | Event | string): string {
        const errorDetails =
            error instanceof Error
                ? `${error.name}: ${error.message}`
                : error instanceof Event
                ? `${error.type} event`
                : String(error);

        return errorDetails;
    }
}

// 导出单例实例
export const errorLogger = ErrorLogger.getInstance();

// 便捷函数
export const reportError = (params: {
    error: Error | Event | string | any;
    componentName: string;
    props?: Record<string, any>;
    state?: Record<string, any>;
    additionalInfo?: Record<string, any>;
}): void => {
    try {
        if (!params || !params.error) {
            return;
        }
        errorLogger.logError(params.error, params.componentName, params.props, params.state, params.additionalInfo);
    } catch (error) {
        console.error('reportError error:', error, 'params:', params);
    }
};
